import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LogIn, Eye, EyeOff, Mail } from 'lucide-react';
import { signIn, resetPassword } from '../../lib/firebase/auth';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';

type Props = {
  onLogin: () => void;
};

export const LoginScreen = ({ onLogin }: Props) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const navigate = useNavigate();
  const { translations, language, setLanguage } = useLanguage();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await signIn(email, password);
      
      localStorage.setItem('userId', response.uid);
      localStorage.setItem('userRole', response.role);
      localStorage.setItem('userName', response.name);
      
      toast.success(translations?.auth?.loginSuccess || 'Login successful');
      setShowSuccessAnimation(true);
      
      setTimeout(() => {
        onLogin();
        navigate('/');
      }, 3000);
      
    } catch (error: any) {
      console.error('Login error:', error);
      toast.error(error?.message || translations?.auth?.loginError || 'Login failed. Please check your email and password.');
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail) {
      toast.error(translations?.auth?.emailRequired || 'Email is required');
      return;
    }
    
    setIsResetting(true);
    try {
      await resetPassword(resetEmail);
      toast.success(translations?.auth?.resetSuccess || 'Password reset email sent. Please check your inbox.');
      setShowResetModal(false);
    } catch (error: any) {
      console.error('Reset password error:', error);
      toast.error(translations?.auth?.resetError || 'Failed to send reset email. Please try again.');
    } finally {
      setIsResetting(false);
    }
  };

  const languages = [
    { code: 'ar', label: 'العربية', flag: '🇸🇦' },
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'it', label: 'Italiano', flag: '🇮🇹' },
    { code: 'fr', label: 'Français', flag: '🇫🇷' }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Language Switcher */}
      <div className="absolute top-4 right-4 z-10">
        <div className="flex gap-2">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => setLanguage(lang.code as 'ar' | 'en' | 'it' | 'fr')}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium transition-colors
                ${language === lang.code 
                  ? 'bg-primary-500 text-white' 
                  : 'bg-white/80 text-gray-700 hover:bg-white shadow-sm'
                }`}
            >
              <span>{lang.flag}</span>
              <span className="hidden sm:inline">{lang.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Success Animation Overlay */}
      <AnimatePresence>
        {showSuccessAnimation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white"
          >
            <div className="flex flex-col items-center justify-center">
              <div className="flex items-center justify-center mb-8">
                <motion.div
                  initial={{ x: -100, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                >
                  <img 
                    src="https://i.ibb.co/jvPWN34G/rehab-logo-1-page-0001-1.png" 
                    alt={translations?.auth?.rehabLogoAlt || 'Rehab Logo'} 
                    className="h-32 object-contain" 
                  />
                </motion.div>
                
                <motion.div
                  initial={{ x: 100, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  className="ml-8"
                >
                  <img 
                    src="https://i.ibb.co/TBjqHtnV/logo-dark.png" 
                    alt={translations?.auth?.ithraaLogoAlt || 'Ithraa Logo'} 
                    className="h-32 object-contain" 
                  />
                </motion.div>
              </div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="text-center"
              >
                <h2 className="text-xl font-bold text-gray-800 mb-2">
                  {translations?.auth?.collaborationMessage || "Powered by our partnership for excellence"}
                </h2>
                <p className="text-gray-600">
                  {translations?.auth?.collaborationSubtext || "Bringing innovation to Hajj services"}
                </p>
              </motion.div>
              
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: "60%" }}
                transition={{ delay: 1, duration: 0.5 }}
                className="mt-12 w-full max-w-md mx-auto relative h-1.5 bg-gray-100 overflow-hidden rounded-full"
              >
                <motion.div
                  initial={{ width: "5%" }}
                  animate={{ width: "100%" }}
                  transition={{ delay: 1, duration: 1.5, ease: "easeInOut" }}
                  className="absolute top-0 left-0 h-full bg-gradient-to-r from-[#4254B4] via-[#00897B] to-[#C8453C] rounded-full"
                />
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100"
          >
            <div className="flex flex-col items-center mb-8">
              <motion.img 
                src="https://i.ibb.co/TBjqHtnV/logo-dark.png"
                alt={translations?.auth?.ithraaLogoAlt || 'Ithraa Al Khair'}
                className="h-20 object-contain mb-6"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 }}
              />
              
              <motion.h1 
                className="text-2xl font-bold text-gray-900 text-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                {translations?.auth?.loginTitle || 'Sign In'}
              </motion.h1>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {translations?.auth?.email || 'Email'}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {translations?.auth?.password || 'Password'}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowResetModal(true)}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  {translations?.auth?.forgotPassword || 'Forgot Password?'}
                </button>
              </div>

              <motion.button
                type="submit"
                disabled={isLoading || showSuccessAnimation}
                className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-white transition-colors ${
                  isLoading || showSuccessAnimation
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-primary-500 hover:bg-primary-600'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <LogIn size={20} />
                <span>
                  {isLoading
                    ? translations?.auth?.loggingIn || 'Signing in...'
                    : translations?.auth?.login || 'Sign In'}
                </span>
              </motion.button>
            </form>
          </motion.div>
          
          <div className="mt-8 text-center text-sm text-gray-500">
            <p>
              <img 
                src="https://i.ibb.co/jvPWN34G/rehab-logo-1-page-0001-1.png"
                alt={translations?.auth?.rehabLogoAlt || 'Rehab Company'}
                className="h-7 inline-block ml-1 object-contain align-middle"
              />
              {translations?.auth?.copyright || '© 2025 All rights reserved to Rehab Company'}
            </p>
            <p className="mt-1">
              {translations?.auth?.poweredBy || 'System developed by Rehab Company'}
            </p>
          </div>
        </div>
      </div>

      {/* Password Reset Modal */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl"
          >
            <h3 className="text-lg font-semibold mb-4">
              {translations?.auth?.resetPassword || 'Reset Password'}
            </h3>
            <p className="text-gray-600 mb-4">
              {translations?.auth?.resetInstructions || 'Enter your email address and we will send you instructions to reset your password.'}
            </p>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {translations?.auth?.email || 'Email'}
                </label>
                <div className="relative">
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className="pl-10 pr-4 py-2 w-full border rounded-lg focus:ring-2 focus:ring-primary-500"
                    required
                  />
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowResetModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  {translations?.auth?.cancel || 'Cancel'}
                </button>
                <button
                  type="submit"
                  disabled={isResetting}
                  className={`px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2`}
                >
                  {isResetting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {translations?.auth?.sending || 'Sending...'}
                    </>
                  ) : (
                    translations?.auth?.sendResetLink || 'Send Reset Link'
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};
